import { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import {
  useAdminDiscoverPreview,
  useAdminDiscoverImport,
  useAdminDiscoverCheck,
  useStartEnrichBatch,
  useEnrichBatchStatus,
  type DiscoverCandidate,
  type ImportCandidate,
} from '../../hooks/useAdmin'
import { geocodeAddress } from '../../lib/geocode'
import { ApiError } from '../../lib/api'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'
import { DataTable } from '../../components/admin/DataTable'
import type { ColumnDef } from '@tanstack/react-table'

const LS_KEY = 'fellowship_discover_candidates'
const LS_ENRICH_KEY = 'fellowship_enrich_batch_id'

// ─── Types + helpers ──────────────────────────────────────────────────────────

type EditableCandidate = DiscoverCandidate & { selected: boolean }
type CandidateFilter = 'all' | 'incomplete' | 'existing'

function isComplete(c: EditableCandidate): boolean {
  return !!c.denomination && !!c.address
}

// ─── Edit modal ───────────────────────────────────────────────────────────────

function CandidateEditModal({
  candidate,
  onSave,
  onClose,
}: {
  candidate: EditableCandidate
  onSave: (osmId: number, updates: Partial<ImportCandidate>) => void
  onClose: () => void
}) {
  const [form, setForm] = useState({
    name: candidate.name,
    denomination: candidate.denomination ?? '',
    address: candidate.address ?? '',
    phone: candidate.phone ?? '',
    email: candidate.email ?? '',
    websiteUrl: candidate.websiteUrl ?? '',
  })

  function set(field: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function handleSave() {
    onSave(candidate.osmId, {
      name: form.name.trim() || candidate.name,
      denomination: form.denomination || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      websiteUrl: form.websiteUrl.trim() || null,
    })
    onClose()
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'var(--surface)',
        borderRadius: 14,
        padding: '28px 32px',
        width: 540,
        maxWidth: '94vw',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 2 }}>Edit church</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{candidate.lat.toFixed(5)}, {candidate.lng.toFixed(5)}</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ModalField label="Name" value={form.name} onChange={(v) => set('name', v)} />
          <div>
            <div style={labelStyle}>Denomination</div>
            <select
              value={form.denomination}
              onChange={(e) => set('denomination', e.target.value)}
              style={modalInput}
            >
              <option value="">— not set —</option>
              {DENOMINATION_OPTIONS.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </div>
          <ModalField label="Address" value={form.address} onChange={(v) => set('address', v)} placeholder="123 Main St, City, ST 12345" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <ModalField label="Phone" value={form.phone} onChange={(v) => set('phone', v)} placeholder="(555) 555-5555" />
            <ModalField label="Email" value={form.email} onChange={(v) => set('email', v)} placeholder="office@church.org" type="email" />
          </div>
          <ModalField label="Website URL" value={form.websiteUrl} onChange={(v) => set('websiteUrl', v)} placeholder="https://www.church.org" type="url" />
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className={tbtn}>Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-1.5 rounded-lg border-0 bg-primary text-on-primary text-[13px] font-semibold font-sans cursor-pointer"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 5,
}

const modalInput: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 7,
  border: '1px solid var(--line)',
  background: 'var(--bg)',
  fontSize: 13,
  fontFamily: 'var(--font-sans)',
  color: 'var(--ink)',
  outline: 'none',
  boxSizing: 'border-box',
}

function ModalField({
  label, value, onChange, placeholder, type = 'text',
}: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string
}) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={modalInput}
      />
    </div>
  )
}

// ─── Candidate table ──────────────────────────────────────────────────────────

const tbtn = 'px-2.5 py-1.5 rounded-[6px] border border-line bg-surface text-[12px] font-sans cursor-pointer text-ink-2 whitespace-nowrap hover:bg-s2'

function cellInput(highlight: boolean, disabled: boolean): React.CSSProperties {
  return {
    width: '100%',
    fontSize: 12.5,
    fontFamily: 'var(--font-sans)',
    border: 'none',
    borderBottom: highlight ? '1.5px solid #f59e0b' : '1px solid transparent',
    background: highlight ? '#fffbeb' : 'transparent',
    color: disabled ? 'var(--muted)' : 'var(--ink)',
    outline: 'none',
    padding: '1px 2px',
    borderRadius: highlight ? '3px 3px 0 0' : 0,
    cursor: disabled ? 'default' : 'text',
  }
}

function CandidateTable({
  candidates,
  onUpdate,
  onUpdateMany,
  onToggle,
  onToggleAll,
  onSelectComplete,
  onSelectNone,
  onSetSelectedOther,
  onExportJSON,
  onImportJSON,
  onImport,
  onEnrich,
  onCheckDB,
  onClear,
  importing,
  checking,
  enrichProgress,
}: {
  candidates: EditableCandidate[]
  onUpdate: (osmId: number, field: keyof ImportCandidate, value: string | null) => void
  onUpdateMany: (osmId: number, updates: Partial<ImportCandidate>) => void
  onToggle: (osmId: number) => void
  onToggleAll: (filteredOsmIds: Set<number>) => void
  onSelectComplete: (filteredOsmIds: Set<number>) => void
  onSelectNone: (filteredOsmIds: Set<number>) => void
  onSetSelectedOther: (filteredOsmIds: Set<number>) => void
  onExportJSON: () => void
  onImportJSON: (e: React.ChangeEvent<HTMLInputElement>) => void
  onImport: () => void
  onEnrich: () => void
  onCheckDB: () => void
  onClear: () => void
  importing: boolean
  checking: boolean
  enrichProgress: { done: number; total: number } | null
}) {
  const [filter, setFilter] = useState<CandidateFilter>('all')
  const [editingCandidate, setEditingCandidate] = useState<EditableCandidate | null>(null)
  const [filteredCandidates, setFilteredCandidates] = useState<EditableCandidate[]>(candidates)
  const jsonInputRef = useRef<HTMLInputElement>(null)

  // Stable refs so column definitions never need to be recreated (preserves input focus)
  const onUpdateRef = useRef(onUpdate); onUpdateRef.current = onUpdate
  const onToggleRef = useRef(onToggle); onToggleRef.current = onToggle
  const onToggleAllRef = useRef(onToggleAll); onToggleAllRef.current = onToggleAll
  const filteredOsmIdsRef = useRef<Set<number>>(new Set())
  const allSelectedRef = useRef(false)

  // Selected count is global (persists across filter changes)
  const selectedCount = candidates.filter((c) => c.selected).length

  // allSelected reflects only the currently filtered/visible newborns
  const filteredOsmIds = useMemo(() => new Set(filteredCandidates.map((c) => c.osmId)), [filteredCandidates])
  filteredOsmIdsRef.current = filteredOsmIds
  const filteredNewborns = filteredCandidates.filter((c) => !c.alreadyExists)
  const allSelected = filteredNewborns.length > 0 && filteredNewborns.every((c) => c.selected)
  allSelectedRef.current = allSelected

  const counts = {
    all: candidates.length,
    incomplete: candidates.filter((c) => !c.alreadyExists && !isComplete(c)).length,
    existing: candidates.filter((c) => c.alreadyExists).length,
  }

  const visible = useMemo(() => {
    if (filter === 'incomplete') return candidates.filter((c) => !c.alreadyExists && !isComplete(c))
    if (filter === 'existing') return candidates.filter((c) => c.alreadyExists)
    return candidates
  }, [candidates, filter])

  const openEdit = useCallback((c: EditableCandidate) => setEditingCandidate(c), [])

  // Stable column definitions — callbacks accessed via refs
  const columns = useMemo<ColumnDef<EditableCandidate, unknown>[]>(() => [
    {
      id: '_select',
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      meta: { skipExport: true, label: '' },
      header: () => (
        <input
          type="checkbox"
          checked={allSelectedRef.current}
          onChange={() => onToggleAllRef.current(filteredOsmIdsRef.current)}
          style={{ cursor: 'pointer' }}
        />
      ),
      cell: ({ row: r }) => (
        <input
          type="checkbox"
          checked={r.original.selected}
          disabled={r.original.alreadyExists}
          onChange={() => onToggleRef.current(r.original.osmId)}
          style={{ cursor: r.original.alreadyExists ? 'not-allowed' : 'pointer' }}
        />
      ),
    },
    {
      accessorKey: 'name',
      header: 'Name',
      meta: { label: 'Name' },
      cell: ({ row: r }) => (
        <input
          type="text"
          value={r.original.name}
          disabled={r.original.alreadyExists}
          onChange={(e) => onUpdateRef.current(r.original.osmId, 'name', e.target.value)}
          style={cellInput(false, r.original.alreadyExists)}
        />
      ),
    },
    {
      accessorKey: 'denomination',
      header: 'Denomination',
      meta: { label: 'Denomination' },
      cell: ({ row: r }) => (
        <select
          value={r.original.denomination ?? ''}
          disabled={r.original.alreadyExists}
          onChange={(e) => onUpdateRef.current(r.original.osmId, 'denomination', e.target.value || null)}
          style={{ ...cellInput(!r.original.alreadyExists && !r.original.denomination, r.original.alreadyExists), cursor: r.original.alreadyExists ? 'not-allowed' : 'pointer' }}
        >
          <option value="">—</option>
          {DENOMINATION_OPTIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
      ),
    },
    {
      accessorKey: 'address',
      header: 'Address',
      meta: { label: 'Address' },
      cell: ({ row: r }) => (
        <input
          type="text"
          value={r.original.address ?? ''}
          disabled={r.original.alreadyExists}
          placeholder="—"
          onChange={(e) => onUpdateRef.current(r.original.osmId, 'address', e.target.value || null)}
          style={cellInput(!r.original.alreadyExists && !r.original.address, r.original.alreadyExists)}
        />
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      meta: { label: 'Phone' },
      cell: ({ row: r }) => (
        <input
          type="text"
          value={r.original.phone ?? ''}
          disabled={r.original.alreadyExists}
          placeholder="—"
          onChange={(e) => onUpdateRef.current(r.original.osmId, 'phone', e.target.value || null)}
          style={cellInput(false, r.original.alreadyExists)}
        />
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      meta: { label: 'Email' },
      cell: ({ row: r }) => (
        <input
          type="text"
          value={r.original.email ?? ''}
          disabled={r.original.alreadyExists}
          placeholder="—"
          onChange={(e) => onUpdateRef.current(r.original.osmId, 'email', e.target.value || null)}
          style={cellInput(false, r.original.alreadyExists)}
        />
      ),
    },
    {
      accessorKey: 'websiteUrl',
      header: 'Website',
      meta: { label: 'Website' },
      cell: ({ row: r }) => (
        <input
          type="text"
          value={r.original.websiteUrl ?? ''}
          disabled={r.original.alreadyExists}
          placeholder="—"
          onChange={(e) => onUpdateRef.current(r.original.osmId, 'websiteUrl', e.target.value || null)}
          style={cellInput(false, r.original.alreadyExists)}
        />
      ),
    },
    {
      id: '_status',
      accessorFn: (r) => r.alreadyExists ? 'indb' : isComplete(r) ? 'ready' : 'incomplete',
      header: 'Status',
      meta: { label: 'Status' },
      enableSorting: true,
      cell: ({ row: r }) => {
        if (r.original.alreadyExists) return <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-[5px] bg-[#fef3c7] text-[#b45309]">In DB</span>
        if (!isComplete(r.original)) return <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-[5px] bg-[#fef3c7] text-[#b45309]">Incomplete</span>
        return <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-[5px] bg-[#dcfce7] text-[#15803d]">Ready</span>
      },
    },
    {
      id: '_edit',
      enableSorting: false,
      enableColumnFilter: false,
      meta: { skipExport: true, label: '' },
      header: () => null,
      cell: ({ row: r }) => (
        <button
          disabled={r.original.alreadyExists}
          onClick={() => !r.original.alreadyExists && openEdit(r.original)}
          title="Edit all fields"
          className="w-7 h-7 flex items-center justify-center rounded-md border border-line bg-surface cursor-pointer text-ink-2 text-[13px] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-s2"
        >
          ✏
        </button>
      ),
    },
  ], [openEdit]) // openEdit is stable via useCallback

  const TABS: { id: CandidateFilter; label: string; amber?: boolean }[] = [
    { id: 'all', label: `All · ${counts.all}` },
    { id: 'incomplete', label: `Missing info · ${counts.incomplete}`, amber: counts.incomplete > 0 },
    { id: 'existing', label: `Already in DB · ${counts.existing}` },
  ]

  // toolbarSlot: filter tabs on the left; right side gets DataTable's Columns button
  const toolbarSlot = (
    <div className="flex gap-1">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => setFilter(t.id)}
          className={`px-2.5 py-1 rounded-md border-0 text-[12px] font-sans cursor-pointer transition-colors ${
            filter === t.id
              ? 'bg-surface font-semibold text-ink shadow-sm border border-line-soft'
              : t.amber
              ? 'bg-transparent text-[#b45309] font-medium hover:bg-amber-50'
              : 'bg-transparent text-ink-2 font-medium hover:bg-s2'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )

  return (
    <>
      {editingCandidate && (
        <CandidateEditModal
          candidate={editingCandidate}
          onSave={(osmId, updates) => { onUpdateMany(osmId, updates); setEditingCandidate(null) }}
          onClose={() => setEditingCandidate(null)}
        />
      )}

      {/* Action toolbar */}
      <div className="flex items-center gap-2 flex-wrap px-1 py-1">
        <span className="text-[13px] font-semibold text-ink">
          {selectedCount} / {candidates.length} selected
        </span>
        <div className="w-px h-4 bg-line mx-1" />
        <button type="button" onClick={() => onToggleAllRef.current(filteredOsmIds)} className={tbtn}>{allSelected ? 'Deselect all' : 'Select all'}</button>
        <button type="button" onClick={() => onSelectComplete(filteredOsmIds)} className={tbtn}>Select ready</button>
        <button type="button" onClick={() => onSelectNone(filteredOsmIds)} className={tbtn}>Select none</button>
        <button
          type="button"
          onClick={() => onSetSelectedOther(filteredOsmIds)}
          disabled={selectedCount === 0}
          className={`${tbtn} disabled:opacity-40`}
          title="Set denomination to 'Other' for all selected churches"
        >
          Set selected → Other
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onEnrich}
          disabled={!!enrichProgress || counts.incomplete === 0}
          title={counts.incomplete === 0 ? 'No incomplete rows to enrich' : `Enrich ${counts.incomplete} incomplete rows`}
          className={`px-2.5 py-1.5 rounded-[6px] text-[12px] font-sans font-semibold whitespace-nowrap transition-opacity ${
            counts.incomplete > 0
              ? 'border-0 text-white cursor-pointer'
              : 'border border-line bg-surface text-muted cursor-not-allowed'
          }`}
          style={counts.incomplete > 0 ? { background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', opacity: enrichProgress ? 0.7 : 1 } : {}}
        >
          {enrichProgress ? `✦ Enriching ${enrichProgress.done}/${enrichProgress.total}…` : `✦ Enrich with AI (${counts.incomplete})`}
        </button>
        <button type="button" onClick={onExportJSON} className={tbtn}>↓ Export JSON</button>
        <button type="button" onClick={() => jsonInputRef.current?.click()} className={tbtn}>↑ Import JSON</button>
        <input ref={jsonInputRef} type="file" accept=".json" onChange={onImportJSON} className="hidden" />
        <button type="button" onClick={onCheckDB} disabled={checking} className={`${tbtn} disabled:opacity-60`}>
          {checking ? '⏳ Checking…' : '⟳ Check DB'}
        </button>
        <button type="button" onClick={onClear} className={`${tbtn} text-muted`}>✕ Clear</button>
        <button
          type="button"
          onClick={onImport}
          disabled={importing || selectedCount === 0}
          className={`px-4 py-1.5 rounded-[7px] border-0 text-[12.5px] font-semibold font-sans whitespace-nowrap transition-opacity ${
            selectedCount > 0 ? 'bg-[#22c55e] text-white cursor-pointer' : 'bg-s2 text-muted cursor-not-allowed'
          } disabled:opacity-70`}
        >
          {importing ? 'Importing…' : `Import ${selectedCount} selected`}
        </button>
      </div>

      <DataTable
        columns={columns}
        data={visible}
        filterable
        configurable
        toolbarSlot={toolbarSlot}
        getRowId={(r) => String(r.osmId)}
        pageSize={100}
        emptyMessage="No candidates match this filter."
        onFilteredRowsChange={setFilteredCandidates}
      />
    </>
  )
}


// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminDiscoverPage({
  hideTitle = false,
  initialLat,
  initialLng,
  initialRadius,
}: {
  hideTitle?: boolean
  initialLat?: number
  initialLng?: number
  initialRadius?: number
}) {
  const hasInitCoords = initialLat != null && initialLng != null
  const [cityQuery, setCityQuery] = useState(
    hasInitCoords ? `${initialLat!.toFixed(4)}, ${initialLng!.toFixed(4)}` : '',
  )
  const [radius, setRadius] = useState(initialRadius ?? 50)
  const [geocoding, setGeocoding] = useState(false)
  const [discoverCoords, setDiscoverCoords] = useState<{ lat: number; lng: number } | null>(
    hasInitCoords ? { lat: initialLat!, lng: initialLng! } : null,
  )
  const [selectedDenominations, setSelectedDenominations] = useState<string[]>([])
  const [candidates, setCandidatesRaw] = useState<EditableCandidate[] | null>(() => {
    try {
      const saved = localStorage.getItem(LS_KEY)
      return saved ? (JSON.parse(saved) as EditableCandidate[]) : null
    } catch {
      return null
    }
  })
  const [error, setError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ imported: number; failed: { name: string; reason: string }[] } | null>(null)
  const [enrichBatchId, setEnrichBatchIdRaw] = useState<string | null>(() => {
    try { return localStorage.getItem(LS_ENRICH_KEY) } catch { return null }
  })

  function setEnrichBatchId(id: string | null) {
    setEnrichBatchIdRaw(id)
    try {
      if (id) localStorage.setItem(LS_ENRICH_KEY, id)
      else localStorage.removeItem(LS_ENRICH_KEY)
    } catch { /* storage unavailable */ }
    // storage event only fires cross-tab; dispatch manually so the sidebar reacts in the same tab
    window.dispatchEvent(new StorageEvent('storage', { key: LS_ENRICH_KEY, newValue: id }))
  }

  // Mirror candidates to localStorage on every change
  function setCandidates(updater: ((prev: EditableCandidate[] | null) => EditableCandidate[] | null) | EditableCandidate[] | null) {
    setCandidatesRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      try {
        if (next === null) localStorage.removeItem(LS_KEY)
        else localStorage.setItem(LS_KEY, JSON.stringify(next))
      } catch { /* storage full or unavailable */ }
      return next
    })
  }

  // Clear stale localStorage when page unmounts only if candidates are all imported
  useEffect(() => {
    return () => {
      // intentional no-op: we want candidates to persist across unmounts (tab switching)
    }
  }, [])

  const preview = useAdminDiscoverPreview()
  const importMutation = useAdminDiscoverImport()
  const startEnrich = useStartEnrichBatch()
  const enrichStatus = useEnrichBatchStatus(enrichBatchId)
  const checkMutation = useAdminDiscoverCheck()
  const jsonInputRef = useRef<HTMLInputElement>(null)

  async function handleGeocode() {
    if (!cityQuery.trim()) return
    setGeocoding(true)
    setError(null)
    const coords = await geocodeAddress(cityQuery.trim())
    setGeocoding(false)
    if (!coords) { setError('Could not find that location.'); return }
    setDiscoverCoords(coords)
  }

  function handlePreview() {
    if (!discoverCoords) return
    setError(null)
    setImportResult(null)
    preview.mutate(
      { lat: discoverCoords.lat, lng: discoverCoords.lng, radiusKm: radius, denominations: selectedDenominations },
      {
        onSuccess: (res) => {
          setCandidates(res.data.map((c) => ({
            ...c,
            email: c.email ?? null,
            selected: !c.alreadyExists && !!c.denomination && !!c.address,
          })))
        },
        onError: (err) => setError(err instanceof ApiError ? err.message : 'Preview failed'),
      },
    )
  }

  function handleLoadJSON() {
    jsonInputRef.current?.click()
  }

  function handleJSONFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string)
        if (!Array.isArray(parsed)) throw new Error('Expected an array')
        const loaded: EditableCandidate[] = (parsed as ImportCandidate[]).map((item, i) => ({
          osmId: -(i + 1),
          osmType: 'node',
          name: item.name ?? '',
          denomination: item.denomination ?? null,
          address: item.address ?? null,
          lat: item.lat,
          lng: item.lng,
          phone: item.phone ?? null,
          email: item.email ?? null,
          websiteUrl: item.websiteUrl ?? null,
          alreadyExists: false,
          selected: !!item.denomination && !!item.address,
        }))
        setCandidates(loaded)
        setImportResult(null)
        setError(null)
      } catch {
        setError('Invalid JSON — expected an array of church objects.')
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  function updateCandidate(osmId: number, field: keyof ImportCandidate, value: string | null) {
    setCandidates((prev) => prev?.map((c) => c.osmId === osmId ? { ...c, [field]: value } : c) ?? null)
  }

  function updateCandidateMany(osmId: number, updates: Partial<ImportCandidate>) {
    setCandidates((prev) =>
      prev?.map((c) => {
        if (c.osmId !== osmId) return c
        const next = { ...c, ...updates }
        return { ...next, selected: !c.alreadyExists && isComplete(next as EditableCandidate) }
      }) ?? null,
    )
  }

  function toggleCandidate(osmId: number) {
    setCandidates((prev) => prev?.map((c) => c.osmId === osmId ? { ...c, selected: !c.selected } : c) ?? null)
  }

  function toggleAll(filteredOsmIds: Set<number>) {
    if (!candidates) return
    const filteredNewborns = candidates.filter((c) => !c.alreadyExists && filteredOsmIds.has(c.osmId))
    const allOn = filteredNewborns.every((c) => c.selected)
    setCandidates((prev) =>
      prev?.map((c) => (!c.alreadyExists && filteredOsmIds.has(c.osmId) ? { ...c, selected: !allOn } : c)) ?? null,
    )
  }

  function selectComplete(filteredOsmIds: Set<number>) {
    setCandidates((prev) =>
      prev?.map((c) => (filteredOsmIds.has(c.osmId) && !c.alreadyExists ? { ...c, selected: isComplete(c) } : c)) ?? null,
    )
  }

  function selectNone(filteredOsmIds: Set<number>) {
    setCandidates((prev) =>
      prev?.map((c) => (filteredOsmIds.has(c.osmId) ? { ...c, selected: false } : c)) ?? null,
    )
  }

  function setSelectedOther(filteredOsmIds: Set<number>) {
    setCandidates((prev) =>
      prev?.map((c) => {
        if (!c.selected || c.alreadyExists || !filteredOsmIds.has(c.osmId)) return c
        const updated = { ...c, denomination: 'other' }
        return { ...updated, selected: isComplete(updated) }
      }) ?? null,
    )
  }

  function handleExportJSON() {
    if (!candidates) return
    const data = candidates.map((c) => ({
      name: c.name,
      denomination: c.denomination,
      address: c.address,
      lat: c.lat,
      lng: c.lng,
      phone: c.phone,
      email: c.email,
      websiteUrl: c.websiteUrl,
    }))
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'churches-discovery.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleImportCandidates(eJson: React.ChangeEvent<HTMLInputElement>) {
    handleJSONFile(eJson)
  }

  function handleCheckDB() {
    if (!candidates) return
    const toCheck = candidates.map((c) => ({ name: c.name, lat: c.lat, lng: c.lng }))
    checkMutation.mutate(toCheck, {
      onSuccess: (res) => {
        const existingSet = new Set(
          res.data.filter((r) => r.alreadyExists).map((r) => `${r.lat.toFixed(5)},${r.lng.toFixed(5)}`),
        )
        setCandidates((prev) =>
          prev?.map((c) => ({
            ...c,
            alreadyExists: existingSet.has(`${c.lat.toFixed(5)},${c.lng.toFixed(5)}`),
            selected: !existingSet.has(`${c.lat.toFixed(5)},${c.lng.toFixed(5)}`) && c.selected,
          })) ?? null,
        )
      },
      onError: () => setError('DB check failed — try again.'),
    })
  }

  function handleImport() {
    if (!candidates) return
    const selectedNames = new Set(candidates.filter((c) => c.selected).map((c) => c.name))
    const selected = candidates.filter((c) => c.selected).map<ImportCandidate>((c) => ({
      name: c.name,
      denomination: c.denomination,
      address: c.address,
      lat: c.lat,
      lng: c.lng,
      phone: c.phone,
      email: c.email ?? null,
      websiteUrl: c.websiteUrl,
    }))
    importMutation.mutate(selected, {
      onSuccess: (res) => {
        setImportResult({ imported: res.data.imported, failed: res.data.failed ?? [] })
        // Mark imported ones as already-in-DB; keep the rest for continued review
        setCandidates((prev) =>
          prev?.map((c) => selectedNames.has(c.name) ? { ...c, alreadyExists: true, selected: false } : c) ?? null,
        )
      },
      onError: (err) => setError(err instanceof ApiError ? err.message : 'Import failed'),
    })
  }

  function handleEnrich() {
    if (!candidates) return
    const incomplete = candidates.filter((c) => !c.alreadyExists && !isComplete(c))
    if (incomplete.length === 0) return
    setError(null)
    startEnrich.mutate(
      incomplete.map((c) => ({
        osmId: c.osmId,
        name: c.name,
        denomination: c.denomination,
        address: c.address,
        lat: c.lat,
        lng: c.lng,
        phone: c.phone,
        email: c.email,
        websiteUrl: c.websiteUrl,
      })),
      {
        onSuccess: (res) => setEnrichBatchId(res.data.batchId),
        onError: () => setError('Failed to start enrichment. Try again.'),
      },
    )
  }

  // Merge enrichment results into candidates as each one finishes
  const prevCompletedRef = useRef(0)
  useEffect(() => {
    const status = enrichStatus.data?.data
    if (!status) return

    const newResults = status.results.slice(prevCompletedRef.current)
    if (newResults.length > 0) {
      const byOsmId = new Map(newResults.map((r) => [r.osmId, r]))
      setCandidates((prev) =>
        prev?.map((c) => {
          const e = byOsmId.get(c.osmId)
          if (!e) return c
          const newDenom = e.denomination ?? c.denomination
          const newAddr = e.address ?? c.address
          return {
            ...c,
            denomination: newDenom,
            address: newAddr,
            phone: e.phone ?? c.phone,
            email: e.email ?? c.email,
            selected: !c.alreadyExists && !!newDenom && !!newAddr,
          }
        }) ?? null,
      )
      prevCompletedRef.current = status.results.length
    }

    if (status.done) {
      setEnrichBatchId(null)
      prevCompletedRef.current = 0
      if (status.failed > 0) {
        setError(`Enrichment finished: ${status.failed} church${status.failed > 1 ? 'es' : ''} failed. Results for the rest have been saved.`)
      }
    }
  }, [enrichStatus.data])

  function toggleDenomination(value: string) {
    setSelectedDenominations((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value],
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      {!hideTitle && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 27, fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 4px', color: 'var(--ink)' }}>
              Church discovery
            </h1>
            <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
              Scan OpenStreetMap for churches near a location, or import a JSON file directly.
              Complete rows (denomination + address) are auto-selected.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <input ref={jsonInputRef} type="file" accept=".json" onChange={handleJSONFile} style={{ display: 'none' }} />
            <button onClick={handleLoadJSON} style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'var(--font-sans)',
              cursor: 'pointer',
              color: 'var(--ink)',
            }}>
              ↑ Import JSON
            </button>
          </div>
        </div>
      )}
      {hideTitle && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <input ref={jsonInputRef} type="file" accept=".json" onChange={handleJSONFile} style={{ display: 'none' }} />
          <button onClick={handleLoadJSON} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid var(--line)',
            background: 'var(--surface)', fontSize: 13, fontWeight: 500,
            fontFamily: 'var(--font-sans)', cursor: 'pointer', color: 'var(--ink)',
          }}>
            ↑ Import JSON
          </button>
        </div>
      )}

      {/* Scan config card */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--line-soft)',
        borderRadius: 12,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          OSM scan
        </div>

        {/* Row 1: location + radius */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6, flex: '1 1 280px', minWidth: 0 }}>
            <input
              type="text"
              value={cityQuery}
              onChange={(e) => setCityQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleGeocode() }}
              placeholder="City or address — e.g. Portland, OR"
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                background: 'var(--bg)',
                fontSize: 13,
                fontFamily: 'var(--font-sans)',
                color: 'var(--ink)',
                outline: 'none',
              }}
            />
            <button
              onClick={() => void handleGeocode()}
              disabled={geocoding || !cityQuery.trim()}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                background: 'var(--surface-2)',
                fontSize: 13,
                fontFamily: 'var(--font-sans)',
                cursor: 'pointer',
                color: 'var(--ink-2)',
                whiteSpace: 'nowrap',
                opacity: geocoding || !cityQuery.trim() ? 0.5 : 1,
              }}
            >
              {geocoding ? '…' : 'Set location'}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 220px' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>Radius</span>
            <input
              type="range"
              min={5} max={200} step={5}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              style={{ flex: 1, accentColor: 'var(--primary)' }}
            />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', width: 50, textAlign: 'right' }}>
              {radius} km
            </span>
          </div>

          <button
            onClick={handlePreview}
            disabled={!discoverCoords || preview.isPending}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: discoverCoords ? 'var(--primary)' : 'var(--surface-2)',
              color: discoverCoords ? 'var(--on-primary, #fff)' : 'var(--muted)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'var(--font-sans)',
              cursor: !discoverCoords || preview.isPending ? 'not-allowed' : 'pointer',
              opacity: preview.isPending ? 0.7 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {preview.isPending ? 'Scanning OSM…' : discoverCoords ? `Scan ${discoverCoords.lat.toFixed(2)}, ${discoverCoords.lng.toFixed(2)}` : 'Set a location first'}
          </button>
        </div>

        {/* Row 2: denominations */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
            Denominations — <span style={{ fontWeight: 400, color: 'var(--muted)' }}>leave all unchecked to discover every Christian church</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
            {DENOMINATION_OPTIONS.map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', fontSize: 13, color: 'var(--ink-2)' }}>
                <input
                  type="checkbox"
                  checked={selectedDenominations.includes(key)}
                  onChange={() => toggleDenomination(key)}
                  style={{ accentColor: 'var(--primary)' }}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {preview.isPending && (
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Querying OpenStreetMap — this can take up to 30s…</div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 16px',
          borderRadius: 8,
          background: 'var(--danger-soft, #fef2f2)',
          border: '1px solid var(--danger-line, #fca5a5)',
          fontSize: 13,
          color: 'var(--danger-deep, #b91c1c)',
        }}>
          {error}
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {importResult.imported > 0 && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              fontSize: 13, color: '#166534', fontWeight: 500,
            }}>
              ✓ {importResult.imported} {importResult.imported === 1 ? 'church' : 'churches'} added to the database.
            </div>
          )}
          {importResult.failed.length > 0 && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              background: '#fef2f2', border: '1px solid #fecaca',
              fontSize: 13, color: '#991b1b',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                ⚠ {importResult.failed.length} {importResult.failed.length === 1 ? 'church' : 'churches'} failed to import:
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {importResult.failed.map((f, i) => (
                  <li key={i} style={{ fontSize: 12 }}>
                    <strong>{f.name}</strong> — {f.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Candidate table or empty state */}
      {candidates !== null ? (
        <CandidateTable
          candidates={candidates}
          onUpdate={updateCandidate}
          onUpdateMany={updateCandidateMany}
          onToggle={toggleCandidate}
          onToggleAll={toggleAll}
          onSelectComplete={selectComplete}
          onSelectNone={selectNone}
          onSetSelectedOther={setSelectedOther}
          onExportJSON={handleExportJSON}
          onImportJSON={handleImportCandidates}
          onImport={handleImport}
          onEnrich={handleEnrich}
          onCheckDB={handleCheckDB}
          onClear={() => setCandidates(null)}
          importing={importMutation.isPending}
          checking={checkMutation.isPending}
          enrichProgress={enrichBatchId
            ? { done: enrichStatus.data?.data.completed ?? 0, total: enrichStatus.data?.data.total ?? 0 }
            : startEnrich.isPending ? { done: 0, total: 0 }
            : null}
        />
      ) : (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 12,
          padding: '48px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>No candidates loaded</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 420, margin: '0 auto' }}>
            Set a location and click <strong>Scan</strong> to query OpenStreetMap, or click <strong>Import JSON</strong> in the top-right to load a previously exported file.
          </div>
        </div>
      )}
    </div>
  )
}
